// La API calcula las acciones e incidentes fiscales de cada factura de vouchers;
// acá solo se presentan.
import { mensajeErrorFiscal, validacionPreviaConfirmada } from "services/facturacionErrores";

export const ESTADOS_FACTURA_FILTRO = [
	{ value: "", label: "Todas" },
	{ value: "EMITIDA", label: "Emitidas" },
	{ value: "PROCESANDO", label: "Procesando" },
	{ value: "ERROR", label: "Con error" },
	{ value: "DESCARTADA", label: "Descartadas" },
];

export const MOTIVO_DESCARTE_MIN = 5;
export const MOTIVO_DESCARTE_MAX = 500;

export const claveOpcionIva = (tratamientoIva, alicuotaIvaId) => (
	tratamientoIva === "GRAVADO" ? `${tratamientoIva}_${alicuotaIvaId}` : tratamientoIva || ""
);

// Intervenciones de una decision de emision en lenguaje claro.
export const INTERVENCION_LABEL = {
	CONFLICTO_REFERENCIA: "la API ya tiene una operación para esta factura y se está buscando",
	INTEGRIDAD_CLAVE: "clave de idempotencia usada con otro contenido (incidente de integridad)",
	OPERACION_NO_COINCIDE: "la operación encontrada en la API no coincide con la factura",
	FALLOS_REPETIDOS: "la API de Facturación falló varias veces seguidas",
	CREDENCIAL_INVALIDA: "credencial o configuración de facturación inválida para la sede",
	ERROR_TECNICO_NO_CLASIFICADO: "error técnico de la API sin reintento automático",
	OPERACION_SUPERADA: "reemplazada por una decisión posterior",
	AUTORIZACION_INESPERADA: "ARCA autorizó una decisión que ya no era la vigente (incidente)",
	CANDIDATO_TOMADO: "el número de comprobante pertenece a otro envío (incidente)",
	INCIDENTE_RESUELTO: "incidente revisado por un operador",
};

export const INCIDENTES_FISCALES = ["AUTORIZACION_INESPERADA", "CANDIDATO_TOMADO"];

export const intervencionLabel = (intervencion) => (intervencion
	? `${INTERVENCION_LABEL[intervencion] || "requiere revisión"} (${intervencion})`
	: "");

// Incluye incidentes de operaciones anteriores, aunque la actual no tenga uno.
export const tieneIncidente = (factura) => Boolean(factura?.acciones?.resolverIncidente)
	|| (factura?.incidentes || []).some((incidente) => INCIDENTES_FISCALES.includes(incidente.intervencion))
	|| INCIDENTES_FISCALES.includes(factura?.estadoFiscal?.intervencion);

// La fecha de emision es la del comprobante autorizado: solo la tiene una EMITIDA.
export const fechaEmisionFactura = (factura, formatear) => (factura?.estado === "EMITIDA" ? formatear(factura) : "—");

export const badgeFactura = (factura) => {
	const incidente = tieneIncidente(factura);
	// Una emitida o descartada con un incidente en otra decision conserva su estado.
	if (factura?.estado === "EMITIDA") return incidente ? { color: "warning", label: "EMITIDA · INCIDENTE" } : { color: "success", label: "EMITIDA" };
	if (factura?.estado === "DESCARTADA") return incidente ? { color: "warning", label: "DESCARTADA · INCIDENTE" } : { color: "secondary", label: "DESCARTADA" };
	if (incidente) return { color: "error", label: "INCIDENTE" };
	if (factura?.estado === "ERROR") return { color: "error", label: "ERROR" };
	if (factura?.estadoFiscal?.requiereIntervencion) return { color: "error", label: "REQUIERE INTERVENCIÓN" };
	return { color: "info", label: factura?.estado || "-" };
};

// Valores iniciales del dialogo de reintento: los de la decision vigente.
export const correccionesIniciales = (factura) => ({
	tipoFactura: factura?.tipoFactura || "",
	actividadCodigo: factura?.actividadCodigo || "",
	opcionIvaClave: factura?.tratamientoIva ? claveOpcionIva(factura.tratamientoIva, factura.alicuotaIvaId) : "",
});

// Solo letra, actividad e IVA: monto, cliente, grupo y sede requieren otra factura
// y el receptor se relee de la ficha del cliente.
export const buildCorrecciones = ({ tipoFactura, actividadCodigo, opcionIva }) => ({
	tipoFactura,
	actividadCodigo,
	tratamientoIva: opcionIva?.tratamientoIva || null,
	alicuotaIvaId: opcionIva?.alicuotaIvaId ?? null,
});

// Un HTTP 500 puede traer un error fiscal válido en el body de GraphQL.
// Apollo lo expone en networkError.result, separado del mensaje de transporte.
const erroresFacturacion = (error) => [error?.graphQLErrors, error?.networkError?.result?.errors, error?.errors]
	.flatMap((items) => (Array.isArray(items) ? items : []));

// Este rechazo ocurre antes de crear la factura: se puede volver a enviar.
// Una falla de transporte sin esta confirmación no habilita una nueva emisión.
export const esValidacionFiscalRechazada = (error) => erroresFacturacion(error).some((item) => (
	(item?.extensions?.code || item?.code) === "CONFIGURACION_INCOMPATIBLE"
)) && validacionPreviaConfirmada(error);

export const esFalloTemporalPrevio = (error) => erroresFacturacion(error).some((item) => {
	const contexto = item?.extensions?.facturacion;
	return contexto?.etapa === "VALIDACION_PREVIA"
		&& contexto.facturaRegistrada === false && contexto.temporal === true;
});

export const mensajeErrorFacturacion = (error, fallback = "No se pudo emitir la factura") => {
	return mensajeErrorFiscal(error, fallback);
};

export const validarMotivoDescarte = (motivo) => {
	const texto = String(motivo || "").trim();
	if (texto.length < MOTIVO_DESCARTE_MIN) return `Indicá el motivo (al menos ${MOTIVO_DESCARTE_MIN} caracteres).`;
	if (texto.length > MOTIVO_DESCARTE_MAX) return `El motivo admite hasta ${MOTIVO_DESCARTE_MAX} caracteres.`;
	return "";
};

const TITULO_BLOQUEO = {
	AUTORIZADA_SIN_APLICAR: "ARCA autorizó una factura que todavía no se registró",
	INCIDENTE: "Hay un incidente fiscal pendiente de revisión",
	PROCESANDO: "Hay una factura pendiente de confirmación",
	OPERACION_ABIERTA: "Hay una operación fiscal abierta",
	ERROR: "Hay una factura con error sin resolver",
};

// Aviso cuando no se puede emitir otra factura por una intencion pendiente.
// Sin permiso de facturar no se sugieren acciones que el usuario no puede hacer.
const accionBloqueo = (motivo, puedeFacturar) => {
	if (!puedeFacturar) return "Pedile a un usuario con permiso de facturación que la resuelva.";
	if (motivo === "ERROR") return "Reintentala o descartala desde el historial.";
	if (motivo === "INCIDENTE") return "Revisalo y marcalo como revisado desde el historial.";
	if (motivo === "PROCESANDO" || motivo === "AUTORIZADA_SIN_APLICAR") return "Usá Verificar en esa factura desde el historial.";
	return "Resolvela desde el historial antes de emitir otra.";
};

export const describirBloqueo = (bloqueo, { puedeFacturar = true } = {}) => (bloqueo
	? {
		titulo: TITULO_BLOQUEO[bloqueo.motivo] || "No se puede emitir otra factura",
		detalle: bloqueo.mensaje,
		accion: accionBloqueo(bloqueo.motivo, puedeFacturar),
	}
	: null);

// Que habilita marcar revisado un incidente, segun su tipo.
export const textosRevisionIncidente = (factura) => {
	const intervenciones = (factura?.incidentes || []).map((incidente) => incidente.intervencion);
	if (factura?.estadoFiscal?.intervencion) intervenciones.push(factura.estadoFiscal.intervencion);
	const textos = [];
	if (intervenciones.includes("AUTORIZACION_INESPERADA")) {
		textos.push("ARCA autorizó un comprobante en una decisión que no era la vigente. Marcarlo como revisado destraba "
			+ "nuevas facturas del grupo, pero esta factura no podrá reintentarse ni descartarse: el comprobante duplicado "
			+ "debe gestionarse fuera del sistema (por ejemplo, con una nota de crédito). Si la decisión vigente sigue abierta "
			+ "y luego falla, se toma ese comprobante como el de la factura.");
	}
	if (intervenciones.includes("CANDIDATO_TOMADO")) {
		textos.push("El número de comprobante pertenece a otro envío. Una vez escalado y revisado, la factura podrá "
			+ "reintentarse con una nueva decisión de emisión (clave nueva) o descartarse.");
	}
	return textos;
};

// La API devuelve el id de la factura que bloquea en extensions.
export const facturaBloqueanteDeError = (error) => {
	const extensions = error?.graphQLErrors?.[0]?.extensions;
	return extensions?.code === "FACTURA_VOUCHER_ABIERTA" ? extensions.idFactura ?? null : null;
};
